import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { StepTypeSchema } from './step-type';

const DuplicateTemplateVariablesSchema = z.object({
  id: z.string(),
  name: z.string(),
  stepType: StepTypeSchema,
});

export type DuplicateTemplateVariables = z.infer<typeof DuplicateTemplateVariablesSchema>;

async function duplicateTemplateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DuplicateTemplateVariables,
) {
  const { id, name, stepType } = DuplicateTemplateVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.post(`/templates/${id}/duplicate`, { name, stepType });
}

export function useDuplicateTemplate() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DuplicateTemplateVariables>({
    mutationFn: variables => duplicateTemplateFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DuplicateTemplateVariables,
    'duplicateTemplate'
  >('duplicateTemplate', mutation);
}
